import React from 'react';
import useCalendarEvent from '../use-calendar-event';
import { IEventCardProps } from '../../utils';
import { ContentContainer, Description, PrimaryInfo, SecondaryInfo, TimeContainer, Title, Wrapper, StyledEventDate, FullEventActionsMenu, EditIcon, TrashIcon } from './styles';
import Labels from '../Labels';
import useEditPageBlock from 'hooks/use-page-block';
import EventTime from './DateTime/Time';
import EventImage from './Image';
import { useDispatch } from 'react-redux';
import { dismissModal, showModal } from 'services/modals';
import { ModalKinds } from 'services/modals/types';
import LiveBadge from 'components/ui/LiveBadge';
import { ICalendarEvent } from 'models/ICalendarEvent';
import { PREDEFINED_TOOLBAR_BUTTONS } from 'components/ui/RichTextEditorV2/utils';
import shortid from 'shortid';

type EditableStringPaths = 'titleRaw' | 'descriptionRaw' | 'image' | 'date';

const FullEventCard: React.FC<IEventCardProps> = ({
  readOnly,
  event,
  onChange,
  onClick,
  showActionsMenu,
  onRemove,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { titleRaw, image, descriptionRaw } = event;
  const { isLive, labels, startDate, when } = useCalendarEvent(event, readOnly);
  const { handleChange } = useEditPageBlock<EditableStringPaths, ICalendarEvent>({
    item: event,
    onChange: (updatedEvent) => onChange?.(updatedEvent),
  });

  const handleCloseEventSettingsModal = () => {
    dispatch(dismissModal(ModalKinds.calendarBlockEventSettings));
  };

  const handleEditEvent = () => {
    const parsedEvent: ICalendarEvent = {
      ...event,
      categories: (event.categories || []).map(category => ({
        ...category,
        id: shortid.generate(),
        label: {
          ...category.label,
          id: shortid.generate(),
        },
      })),
    };

    dispatch(showModal({
      kind: ModalKinds.calendarBlockEventSettings,
      data: {
        locked: true,
        calendarEvent: parsedEvent,
        onDismiss: handleCloseEventSettingsModal,
        onSave: (updatedEvent) => onChange?.(updatedEvent),
      },
    }));
  };

  const handleRemoveEvent = React.useCallback(() => {
    onRemove?.(event);
  }, [event, onRemove]);

  return (
    <Wrapper {...rest} firstLabelColor={labels[0]?.color} onClick={onClick}>
      {image.show && <EventImage readOnly={readOnly} image={event.image} onChange={handleChange('image')} />}
      {
        showActionsMenu && (
          <FullEventActionsMenu
            admin={false}
            actions={[
              {
                onClick: handleEditEvent,
                stringKey: 'ADMIN_CALENDAR_BLOCK_EDIT_EVENT',
                icon: <EditIcon />,
              },
              {
                onClick: handleRemoveEvent,
                stringKey: 'ADMIN_CALENDAR_BLOCK_DELETE_EVENT',
                icon: <TrashIcon />,
              },
            ]}
            testId="full-event-card-actions-menu"
          />
        )
      }
      <ContentContainer showImage={image.show}>
        <PrimaryInfo>
          <StyledEventDate
            readOnly={readOnly}
            date={event.date}
            startDate={startDate}
            onChange={handleChange('date')}
          />
          <Title
            readOnly={readOnly}
            data={titleRaw}
            onChange={handleChange('titleRaw')}
            blockTitleKey="ADMIN_LABEL_TITLE"
            maxLines={3}
            maxLength={72}
            toolbarButtons={{
              ...PREDEFINED_TOOLBAR_BUTTONS.titleEditor,
              bold: false,
              fontsize: false,
            }}
            data-testid="calendarEventTitle"
          />
          <Description
            readOnly={readOnly}
            data={descriptionRaw}
            onChange={handleChange('descriptionRaw')}
            blockTitleKey="ADMIN_LABEL_DESCRIPTION"
            maxLines={3}
            maxLength={250}
            toolbarButtons={{
              ...PREDEFINED_TOOLBAR_BUTTONS.bodyEditor,
              bold: false,
              fontsize: false,
            }}
            data-testid="calendarEventDescription"
          />
        </PrimaryInfo>
        <SecondaryInfo>
          <TimeContainer>
            <EventTime
              readOnly={readOnly}
              date={event.date}
              onChange={handleChange('date')}
              when={when}
            />
            {isLive && readOnly && <LiveBadge.Default />}
          </TimeContainer>
          <Labels labels={labels} />
        </SecondaryInfo>
      </ContentContainer>
    </Wrapper>
  );
};

export default FullEventCard;
